<template>
  <!--
    Outer container used so we can place our two images vertically on top of one another.
    Padding is added to bottom of container based on width of container and calculated
    height:width ratio of placeholder image.
  -->
  <div class="outer-container" ref="container" v-bind:style="outerDivStyle" :data-identifier="logId">
    <!--
      Placeholder and full-size images are only shown one at a time. The transition allows them
      to be swapped in a visually pleasing way. The beforeFullSizeShow() function is run
      just before the full size image is shown, and it takes care of adding the image data URL.
    -->
    <transition v-on:before-enter="beforeFullSizeShow" :duration="100">
      <!--
        Shown when "this.fullSizeImageLoaded" is true (set automatically after download.
        Notes:
         - "ref" lets us access the element via "this.$refs.<ref>"
         - "key" required for transitions with two of the same type of element.
      -->
      <img
              v-if="fullSizeImageLoaded"
              ref="fullSize"
              key="fullSize"
              class="full-size"
              :alt="alt"
              width="100%"
              height="100%"

      />
      <!-- Shown when "this.fullSizeImageLoaded" is false -->
      <img
              v-else
              ref="placeholder"
              key="placeholder"
              class="placeholder"
              :srcset="lazyPlaceholder"
              :alt="alt"
              width="100%"
              height="100%"

      />
    </transition>
  </div>
</template>

<script>

    import VisibilityChangeAnnouncer from '../visibility-change-announcer.js';

    export default {
        name: 'DynamicImage',
        props: {
            /** URL of the image. Same value as if loading to an <img>. */
            src: {
                type: String,
                required: true
            },
            /** Alternate text describing the image. Same value as if loading to <img>. */
            alt: {
                type: String,
                required: true
            },
            /** Used in place of "class" to pass classes to the internal image. */
            imgClass: {
                type: String,
                required: false
            },
        },
        data: function() {
            return {
                /**
                 * Indicates whether or not the full-size version of
                 * the image has been loaded.
                 * @type {Boolean}
                 */
                fullSizeImageLoaded: false,

                /**
                 * If true, the image has started downloading and
                 * another download shouldn't begin.
                 */
                downloadInProgress: false,

                /**
                 * The dimensions of the image container should only be
                 * fixed once.
                 */
                dimensionFixDone: false,
                /**
                 * Data URL of full-size image once loaded.
                 */
                dataUrl: null,

                /**
                 * The height of the container as percentage of width. Computed when
                 * placeholder image is loaded.
                 */
                heightPercent: 0,

                width: null,
                height: null,

                /**
                 * So that logs from this instance may be distinguished, generate a
                 * random hash-like ID that will be used for all log messages.
                 */
                logId: Math.random().toString(36).substring(2, 5)
            }
        },
        computed: {
            /**
             * The placeholder image URL to be displayed while the full
             * size variant is loading.
             * @type {String}
             */
            lazyPlaceholder: function () {
                let image1x = this.addSuffix(this.processedSrc, "lazy");
                let image2x = this.addSuffix(this.processedSrc, "lazy@2x");

                return image1x + " 1x," + image2x + " 2x";
            },

            /**
             * Processes the provided src to make sure it is in the correct form.
             * @return {string} The processed source.
             */
            processedSrc: function () {
                return this.src.replace(" ", ""); // Path should not have spaces.
            },

            outerDivStyle() {
                let styleObject = {
                    paddingBottom: `${this.heightPercent}%`
                };

                if(this.height && this.width) {
                    styleObject.height = this.height;
                    styleObject.width = this.width;
                }
                return styleObject;
            },
        },
        methods: {

            /**
             * Helper function to inject a suffix into the image path.
             * Acceptable suffixes include "lazy" (which indicates that
             * the minimally-sized blurry placeholder should be loaded,
             * and a number, which will cause an an image sized near
             * to this to be loaded.
             * Examples:
             * - /path/to/image.png => /path/to/image.png?size=lazy
             * - /path/to/image.png => /path/to/image.png?size=1500
             * @param {String} path The path to modify.
             * @param {String|Number} suffix The type of image load.
             * @return {String} The modified path.
             */
            addSuffix: function (path, suffix) {
                return path + "?size=" + suffix;
            },

            /**
             * Causes the full-sized version of the image displayed by
             * this component to be downloaded asynchronously and loaded
             * into the DOM when ready.
             * @param {Boolean} visible Whether or not the component is visible.
             */
            downloadFullSizeImage: function (visible) {

                // Don't re-download if already loaded.
                if(this.fullSizeImageLoaded) {
                    this.debugLog("Full-size image already loaded. Not downloading.");
                    return;
                }

                // Don't download if already downloading.
                if(this.downloadInProgress) {
                    this.debugLog("Download already in progress. Not starting another.");
                    return;
                }
                this.downloadInProgress = true;
                this.debugLog("Preparing download...");

                let xhr = new XMLHttpRequest();
                xhr.onreadystatechange = function() {
                    if (xhr.readyState === XMLHttpRequest.DONE) {
                        if(xhr.status === 200) {
                            this.debugLog("Download completed successfully. Loading image to page.");
                            this.dataUrl = this.createDataUrl(xhr);
                            if(this.$refs.placeholder.complete) {
                                this.debugLog("Placeholder loaded - inserting full-size image.");
                                this.fixContainerDimensions(); // Be sure dimensions have been updated.
                                this.updateImage();
                            } else {
                                this.debugLog("Placeholder not loaded - will insert full-size when loaded.");
                            }
                        } else {
                            this.errorLog("Download failed. Status: " + xhr.status + " (" + xhr.statusText + ").");
                            this.downloadInProgress = false;
                        }
                    }
                }.bind(this);
                xhr.responseType = "arraybuffer";
                let width = this.$el.offsetWidth;
                let newSize = this.shouldLoadHighDensityImage ? width * 2 : width;

                this.debugLog("Starting download.");
                xhr.open("GET", this.addSuffix(this.processedSrc, newSize), true);
                xhr.send();
            },

            /**
             * Updates the <img> component with the newly loaded data URL.
             * @param {String} dataUrl The Data URL to load into the image src.
             */
            updateImage: function () {
                this.fullSizeImageLoaded = true;
                this.downloadInProgress = false;
            },

            /**
             * Helper function to construct a data URL from the provided
             * XHR. Called after the XMLHttpRequest returns successfully.
             * @param {XMLHttpRequest} xhr The request object.
             * @return {string} The data URL.
             */
            createDataUrl: function (xhr) {
                let blob = new Blob([xhr.response], {
                    type: xhr.getResponseHeader("Content-Type")
                });
                return window.URL.createObjectURL(blob);
            },

            /**
             * Detects whether or not the high density image should be loaded.
             * This gives the browser the opportunity to give an opinion.
             * @return {boolean}
             */
            shouldLoadHighDensityImage: function () {
                return false;//this.$refs.placeholder.currentSrc.includes("@2x");
            },

            /**
             * Executed just before new image is shown, so we can set the image data.
             */
            beforeFullSizeShow: function ($el) {
                $el.src = this.dataUrl;
            },

            scheduleFixContainerDimensions () {
                this.$refs.placeholder.addEventListener("load", this.fixContainerDimensionsHandler.bind(this));
            },

            fixContainerDimensions () {
                if(this.dimensionFixDone) return; // Early-exit if already complete.
                let placeholder = this.$refs.placeholder;
                if(!placeholder.complete || placeholder.naturalWidth === 0 || placeholder.naturalHeight === 0)  {
                    // Image not full loaded, wait until it is.
                    this.debugLog("Placeholder not yet loaded - scheduling fix.");
                    this.scheduleFixContainerDimensions();
                } else {
                    // Image fully loaded, no need to wait.
                    this.debugLog("Placeholder loaded - fixing container dimensions.");
                    this.fixContainerDimensionsHandler();
                }
            },

            /**
             * Once placeholder loaded, compute height of container div from
             * width multiplied by height:width ratio.
             * Saving this to the "this.height" property automatically
             * re-calculates the inline style of container.
             */
            fixContainerDimensionsHandler: function () {
                this.debugLog("Attempting to fix dimensions.");
                let placeholder = this.$refs.placeholder;
                if(placeholder) {
                    this.debugLog("Placeholder loaded: true");
                    this.debugLog(`Placeholder complete: ${placeholder.complete}`);

                    let height = placeholder.naturalHeight;
                    let width = placeholder.naturalWidth;
                    this.debugLog(`Placeholder dimensions: [w:${width}/h:${height}]`);

                    let ratio = placeholder.naturalHeight / placeholder.naturalWidth;
                    this.debugLog("Computed ratio: " + ratio);
                    this.heightPercent = ratio * 100;

                    if(this.dataUrl && !this.fullSizeImageLoaded) {
                        this.debugLog("Dimensions fixed - inserting full-size image.");
                        this.updateImage();
                    }
                    this.dimensionFixDone = true;
                } else {
                    this.errorLog("Placeholder null on height fix.");
                }

            },

            debugLog: function (message) {
                console.debug(this.createLogMessage(message));
            },

            errorLog: function (message) {
                console.error(this.createLogMessage(message));
            },

            createLogMessage: function (message) {
                return "[" + this.logId + "] " + message;
            }
        },

        /**
         * Executed when the component is mounted. At this point, we can access elements in the component.
         */
        mounted: function () {
            // We may be able to start the download if the full-size image -- if the outer container
            // has a width.
            setTimeout(this.downloadFullSizeImage.bind(this), 50);

            // As soon as we have a placeholder, we can adjust the container dimensions.
            this.fixContainerDimensions();
        },
    }
</script>

<!-- Scoped styles only apply to elements in this component. -->
<style scoped>
  .placeholder {
    filter: blur(15px);
    top: 0;
    left: 0;
    position: absolute;
    height: 100%;
    width: 100%;
  }

  .full-size {
    top: 0;
    left: 0;
    position: absolute;
  }

  .outer-container {
    position: relative;
    height: 100%;
    width: 100%;
    display: block;
  }
</style>